<template>
    <form-wrapper class="intake-treatment" @submit="handleFormSubmission(data)">
        <p v-if="!data.treatments.length" class="intake-treatment__empty">
            {{ $t('forms.intake.treatment_received.empty') }}
        </p>

        <div v-for="(treatment, index) in data.treatments" :key="`treatment-${index}`" class="intake-treatment__card">
            <div class="intake-treatment__card-header">
                <h4 class="intake-treatment__card-title">
                    {{ $t('forms.intake.treatment_received.treatment_label', { number: index + 1 }) }}
                </h4>
                <common-button
                    :label="$t('common.microcopy.remove')"
                    type="secondary"
                    size="small"
                    :isSubmittable="false"
                    @click="removeTreatment(index)"
                ></common-button>
            </div>

            <form-row>
                <form-cell>
                    <select-field
                        :label="$t('forms.intake.treatment_received.fields.treatment_type.label')"
                        :placeholder="$t('forms.intake.treatment_received.fields.treatment_type.placeholder')"
                        :isRequired="true"
                        :options="treatmentTypeOptions"
                        :formData="data"
                        :valuePath="`treatments.${index}.treatmentType`"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.treatment_received.fields.provider_name.label')"
                        :placeholder="$t('forms.intake.treatment_received.fields.provider_name.placeholder')"
                        :formData="data"
                        :valuePath="`treatments.${index}.providerName`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.treatment_received.fields.facility.label')"
                        :placeholder="$t('forms.intake.treatment_received.fields.facility.placeholder')"
                        :formData="data"
                        :valuePath="`treatments.${index}.facility`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <div class="intake-treatment__regions">
                <label class="form-field__label">
                    {{ $t('forms.intake.treatment_received.fields.body_regions.label') }}
                </label>
                <div class="intake-treatment__regions-grid">
                    <checkbox-input
                        v-for="option in bodyRegionOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="isBodyRegionChecked(index, option.value)"
                        @change="toggleBodyRegion(index, option.value)"
                    ></checkbox-input>
                </div>
            </div>

            <form-row>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.treatment_received.fields.start_date.label')"
                        type="date"
                        :formData="data"
                        :valuePath="`treatments.${index}.startDate`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.treatment_received.fields.end_date.label')"
                        type="date"
                        :isDisabled="Boolean(data.treatments[index].stillReceiving)"
                        :formData="data"
                        :valuePath="`treatments.${index}.endDate`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <checkbox-field
                        :formData="data"
                        :valuePath="`treatments.${index}.stillReceiving`"
                        @change="onStillReceivingChange(index, $event)"
                    >
                        <template #label>
                            <span>{{ $t('forms.intake.treatment_received.fields.still_receiving.label') }}</span>
                        </template>
                    </checkbox-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.treatment_received.fields.frequency.label')"
                        :placeholder="$t('forms.intake.treatment_received.fields.frequency.placeholder')"
                        :formData="data"
                        :valuePath="`treatments.${index}.frequency`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.intake.treatment_received.fields.helped.label')"
                        :placeholder="$t('forms.intake.treatment_received.fields.helped.placeholder')"
                        :options="helpedOptions"
                        :formData="data"
                        :valuePath="`treatments.${index}.helped`"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <text-field
                        :label="$t('forms.intake.treatment_received.fields.relief_duration.label')"
                        :placeholder="$t('forms.intake.treatment_received.fields.relief_duration.placeholder')"
                        :formData="data"
                        :valuePath="`treatments.${index}.reliefDuration`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <text-field
                        :label="$t('forms.intake.treatment_received.fields.stop_reason.label')"
                        :placeholder="$t('forms.intake.treatment_received.fields.stop_reason.placeholder')"
                        :formData="data"
                        :valuePath="`treatments.${index}.stopReason`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <textarea-field
                        :label="$t('forms.intake.treatment_received.fields.additional_details.label')"
                        :placeholder="$t('forms.intake.treatment_received.fields.additional_details.placeholder')"
                        :formData="data"
                        :valuePath="`treatments.${index}.additionalDetails`"
                        @change="change($event)"
                    ></textarea-field>
                </form-cell>
            </form-row>
        </div>

        <button class="intake-treatment__add" type="button" @click="addTreatment">
            + {{ $t('forms.intake.treatment_received.actions.add_treatment') }}
        </button>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'

    const BODY_REGIONS = [
        'head',
        'neck',
        'upper_back',
        'mid_back',
        'low_back',
        'right_shoulder',
        'left_shoulder',
        'right_arm',
        'left_arm',
        'right_elbow',
        'left_elbow',
        'right_wrist_hand',
        'left_wrist_hand',
        'chest',
        'abdomen',
        'right_hip',
        'left_hip',
        'right_thigh',
        'left_thigh',
        'right_knee',
        'left_knee',
        'right_leg',
        'left_leg',
        'right_ankle_foot',
        'left_ankle_foot',
        'other',
    ]

    const TREATMENT_TYPES = [
        'emergency_care',
        'primary_care',
        'orthopedic_care',
        'neurosurgical_care',
        'neurology',
        'pain_management',
        'physical_therapy',
        'chiropractic',
        'acupuncture',
        'massage_therapy',
        'medications',
        'injections',
        'surgery',
        'other',
    ]

    const HELPED_OPTIONS = [
        'no_improvement',
        'temporary_improvement',
        'some_improvement',
        'significant_improvement',
        'made_worse',
        'unsure',
    ]

    export default {
        mixins: [FormMixin],

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            return {
                data: {
                    treatments: [],
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            treatmentTypeOptions() {
                return TREATMENT_TYPES.map((value) => ({
                    label: this.$t(`forms.intake.treatment_received.fields.treatment_type.options.${value}`),
                    value,
                }))
            },

            bodyRegionOptions() {
                return BODY_REGIONS.map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.body_region.options.${value}`),
                    value,
                }))
            },

            helpedOptions() {
                return HELPED_OPTIONS.map((value) => ({
                    label: this.$t(`forms.intake.treatment_received.fields.helped.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            createTreatment() {
                return {
                    treatmentType: undefined,
                    providerName: undefined,
                    facility: undefined,
                    bodyRegions: [],
                    startDate: undefined,
                    endDate: undefined,
                    stillReceiving: false,
                    frequency: undefined,
                    helped: undefined,
                    reliefDuration: undefined,
                    stopReason: undefined,
                    additionalDetails: undefined,
                }
            },

            addTreatment() {
                this.change({
                    path: 'treatments',
                    value: [...this.data.treatments, this.createTreatment()],
                })
            },

            removeTreatment(index) {
                const next = [...this.data.treatments]
                next.splice(index, 1)
                this.change({ path: 'treatments', value: next })
            },

            isBodyRegionChecked(index, region) {
                const regions = this.data.treatments[index]?.bodyRegions || []
                return regions.includes(region)
            },

            toggleBodyRegion(index, region) {
                const current = [...(this.data.treatments[index]?.bodyRegions || [])]
                const nextRegions = current.includes(region) ? current.filter((item) => item !== region) : [...current, region]

                this.change({
                    path: `treatments.${index}.bodyRegions`,
                    value: nextRegions,
                })
            },

            onStillReceivingChange(index, event) {
                this.change(event)
                if (event.value) {
                    this.change({
                        path: `treatments.${index}.endDate`,
                        value: undefined,
                    })
                }
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .intake-treatment__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-treatment__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: #fff;

        @include breakpoint(tablet) {
            padding: rem(12px);
        }
    }

    .intake-treatment__card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(12px);
        flex-wrap: wrap;
    }

    .intake-treatment__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-treatment__regions {
        margin: rem(20px) 0;
    }

    .intake-treatment__regions-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: rem(10px) rem(12px);
        margin-top: rem(10px);

        @include breakpoint(tablet) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @include breakpoint(smartphone) {
            grid-template-columns: 1fr;
        }
    }

    .intake-treatment__add {
        display: block;
        width: 100%;
        margin-top: rem(4px);
        padding: rem(14px) rem(12px);
        border: rem(1px) dashed $color--accent-1;
        border-radius: rem($border-radius--base);
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: rgba($color--accent-1, 0.06);
        }
    }
</style>
